import {
  createSlice,
  createAsyncThunk,
  PayloadAction,
} from "@reduxjs/toolkit";

import {
  doc,
  getDoc,
  updateDoc,
  serverTimestamp,
  collection,
  onSnapshot,
  query,
  where,
  getDocs,
} from "firebase/firestore";

import { db } from "../firebase";
import { AppDispatch, RootState } from "./store";

/* ──────────────────────────
   TYPES
   ────────────────────────── */

export interface ProductOrder {
  id: string;
  guestId: string;
  hostId: string;

  items: any[];
  subtotal: number;
  totalQty: number;
  totalPaid: number;

  guestsFee: number;
  hostsFee: number;
  hostProfit: number;
  platformProfit: number;

  paymentId: string;

  isConfirmed: boolean;
  isCancelled: boolean;
  isRefunded: boolean;
  isPaid: boolean;
  isDelivered: boolean;

  createdAt?: any;
  updatedAt?: any;

  [key: string]: any;
}

interface ProductOrdersState {
  orders: ProductOrder[];
  currentOrder: ProductOrder | null;
  error: string | null;
  unsubscribe?: () => void;

  hostDeliveredCount: number;
  hostDeliveredProfit: number;
}

/* ──────────────────────────
   INITIAL STATE
   ────────────────────────── */

const initialState: ProductOrdersState = {
  orders: [],
  currentOrder: null,
  error: null,
  unsubscribe: undefined,
  hostDeliveredCount: 0,
  hostDeliveredProfit: 0,
};

export const upsertProductOrder = createAsyncThunk<
  ProductOrder,
  Partial<ProductOrder> & {
    paymentId: string;
    hostId?: string;
    deliveryStatus?: {
      sellerDelivered?: boolean;
      buyerReceived?: boolean;
      orderDelivered?: boolean;
    };
  },
  { rejectValue: string }
>(
  "GeneralCommerceOrders/upsert",
  async (orderData, { rejectWithValue }) => {
    try {
      if (!orderData.paymentId)
        throw new Error("Missing paymentId");

      const orderId = orderData.paymentId;
      const docRef = doc(db, "GeneralCommerceOrders", orderId);

      const snap = await getDoc(docRef);
      if (!snap.exists()) throw new Error("Order does not exist");

      const existing = snap.data() as ProductOrder;

      const updatePayload: any = {
        updatedAt: serverTimestamp(),
      };

      /* UPDATE ITEMS PER HOST */

      if (orderData.deliveryStatus) {
        const updatedItems = existing.items.map((item: any) => {
          // HOST marking shipped
          if (
            orderData.hostId &&
            item.hostId === orderData.hostId &&
            orderData.deliveryStatus?.sellerDelivered === true
          ) {
            return {
              ...item,
              deliveryStatus: {
                ...item.deliveryStatus,
                sellerDelivered: true,
              },
            };
          }

          // GUEST marking received
          if (
            orderData.listingId &&
            item.listingId === orderData.listingId &&
            orderData.deliveryStatus?.buyerReceived === true
          ) {
            return {
              ...item,
              deliveryStatus: {
                ...item.deliveryStatus,
                buyerReceived: true,
                orderDelivered: true,
              },
            };
          }

          return item;
        });

        updatePayload.items = updatedItems;

        // Check if ALL items fully delivered
        const allDelivered = updatedItems.every(
          (i: any) =>
            i.deliveryStatus?.sellerDelivered === true &&
            i.deliveryStatus?.buyerReceived === true
        );

        if (allDelivered) {
          updatePayload.isDelivered = true;
        }
      }

      await updateDoc(docRef, updatePayload);

      return {
        ...existing,
        ...updatePayload,
        id: orderId,
      };
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to upsert product order"
      );
    }
  }
);

export const listenToProductOrder =
  (orderId: string) =>
  (dispatch: AppDispatch) => {
    const docRef = doc(db, "GeneralCommerceOrders", orderId);

    const unsubscribe = onSnapshot(
      docRef,
      (snap) => {
        if (snap.exists()) {
          const data = snap.data() as ProductOrder;

          const { id: _ignored, ...rest } = data;

          dispatch(
            productsOrderSlice.actions.setCurrentOrder({
              id: snap.id,
              ...rest,
              createdAt: rest.createdAt?.toMillis?.() || null,
              updatedAt: rest.updatedAt?.toMillis?.() || null,
            })
          );
        } else {
          dispatch(productsOrderSlice.actions.setCurrentOrder(null));
        }
      },
      (error) => {
        dispatch(productsOrderSlice.actions.setError(error.message));
      }
    );

    dispatch(productsOrderSlice.actions.setUnsubscribe(unsubscribe));
  };

export const listenToProductGuestOrders =
  (guestId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!guestId) return;

    const { unsubscribe } = getState().productOrders;
    if (unsubscribe) return;

    const q = query(
      collection(db, "GeneralCommerceOrders"),
      where("guestId", "==", guestId)
    );

    const unsubscribeFn = onSnapshot(
      q,
      (snap) => {
        const orders: ProductOrder[] = snap.docs.map((d) => {
          const data = d.data() as ProductOrder;

          const { id: _ignored, ...rest } = data;

          return {
            id: d.id,
            ...rest,
            createdAt: rest.createdAt?.toMillis?.() || null,
            updatedAt: rest.updatedAt?.toMillis?.() || null,
          };
        });

        dispatch(productsOrderSlice.actions.setOrders(orders));
      },
      (error) => {
        dispatch(productsOrderSlice.actions.setError(error.message));
      }
    );

    dispatch(productsOrderSlice.actions.setUnsubscribe(unsubscribeFn));
  };

export const listenToProductHostOrders =
  (hostId: string) =>
  (dispatch: AppDispatch, getState: () => RootState) => {
    if (!hostId) return;

    const { unsubscribe } = getState().productOrders;
    if (unsubscribe) return;

    // 🔥 Fetch only confirmed orders
    const q = query(
      collection(db, "GeneralCommerceOrders"),
      where("isConfirmed", "==", true)
    );

    const unsubscribeFn = onSnapshot(
      q,
      (snap) => {
        const orders: ProductOrder[] = snap.docs
          .map((docSnap) => {
            const raw = docSnap.data() as ProductOrder;

            // 🔥 Remove id from Firestore data to avoid overwrite warning
            const { id: _ignored, ...rest } = raw;

            // 🔥 Filter only items belonging to this host
            const hostItems = rest.items?.filter(
              (item: any) => item.hostId === hostId
            );

            // ❌ Skip order if no items for this host
            if (!hostItems || hostItems.length === 0) return null;

            return {
              id: docSnap.id, // safe id
              ...rest,
              items: hostItems, // only this host's products
              createdAt: rest.createdAt?.toMillis?.() ?? null,
              updatedAt: rest.updatedAt?.toMillis?.() ?? null,
            };
          })
          .filter(Boolean) as ProductOrder[];

        dispatch(productsOrderSlice.actions.setOrders(orders));
      },
      (error) => {
        dispatch(productsOrderSlice.actions.setError(error.message));
      }
    );

    dispatch(productsOrderSlice.actions.setUnsubscribe(unsubscribeFn));
  };

/* ──────────────────────────
   HOST STATS
   ────────────────────────── */

export const fetchProductHostDeliveredStats = createAsyncThunk<
  {
    total: number;
    delivered: number;
    pending: number;
    profit: number;
  },
  string,
  { rejectValue: string }
>(
  "productOrders/fetchHostDeliveredStats",
  async (hostId, { rejectWithValue }) => {
    try {
      if (!hostId) throw new Error("Missing hostId");

      const q = query(
        collection(db, "GeneralCommerceOrders"),
        where("hostId", "==", hostId)
      );

      const snap = await getDocs(q);

      let total = 0;
      let delivered = 0;
      let pending = 0;
      let profit = 0;

      snap.forEach((doc) => {
        const data = doc.data() as ProductOrder;

        total++;

        if (data.isDelivered === true) {
          delivered++;
          profit += data.hostProfit || 0;
        } else {
          pending++;
        }
      });

      return { total, delivered, pending, profit };
    } catch (err: any) {
      return rejectWithValue(
        err.message || "Failed to fetch product stats"
      );
    }
  }
);

/* ──────────────────────────
   SLICE
   ────────────────────────── */

const productsOrderSlice = createSlice({
  name: "productOrders",
  initialState,
  reducers: {
    setOrders: (state, action: PayloadAction<ProductOrder[]>) => {
      state.orders = action.payload;
    },
    setCurrentOrder: (state, action: PayloadAction<ProductOrder | null>) => {
      state.currentOrder = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setUnsubscribe: (
      state,
      action: PayloadAction<(() => void) | undefined>
    ) => {
      state.unsubscribe = action.payload;
    },
    clearOrders: (state) => {
      state.orders = [];
      state.currentOrder = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(upsertProductOrder.fulfilled, (state, action) => {
      const idx = state.orders.findIndex((o) => o.id === action.payload.id);

      if (idx >= 0) {
        state.orders[idx] = action.payload;

        if (state.currentOrder?.id === action.payload.id) {
          state.currentOrder = action.payload;
        }
      } else {
        state.orders.unshift(action.payload);
      }
    });

    builder.addCase(upsertProductOrder.rejected, (state, action) => {
      state.error = action.payload || "Failed to upsert product order";
    });

    builder.addCase(
      fetchProductHostDeliveredStats.fulfilled,
      (state, action) => {
        state.hostDeliveredCount = action.payload.delivered;
        state.hostDeliveredProfit = action.payload.profit;
      }
    );
  },
});

export const { setOrders, setCurrentOrder, setError, setUnsubscribe, clearOrders } =
  productsOrderSlice.actions;

export default productsOrderSlice.reducer;
